import React, { useState, useRef, useCallback } from 'react';
import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import {
  useMediaQuery,
  List,
  Popover,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  ButtonBase,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';

import { SERVICES } from '../constants/appConstants';
import EngineSelect from './engineSelect';
import ServiceSelect from './ServiceSelect';

// Import engine icons
import guruLogo from '../assets/logos/guru-icon.svg';
import openaiLogo from '../assets/logos/chatgpt-icon.svg';
import claudeLogo from '../assets/logos/claude-ai-icon.svg';
import geminiLogo from '../assets/logos/google-gemini-icon.svg';
import deepseekLogo from '../assets/logos/deepseek_logo.svg';
import grokLogo from '../assets/logos/grok.svg';

// Icons for services
import AdjustIcon from '@mui/icons-material/Tune';
import OptimizeIcon from '@mui/icons-material/Psychology';
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';

const SelectContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-around',
  height: '100%',
  padding: theme.spacing(1),
}));

const MobileSelectionButton = styled(ButtonBase)(({ theme, selected, disabled }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(1),
  marginBottom: theme.spacing(1),
  backgroundColor: 'transparent',
  border: `1px solid ${selected ? theme.palette.primary.main : theme.palette.divider}`,
  opacity: disabled ? 0.5 : 1,
  cursor: disabled ? 'default' : 'pointer',
  height: 40,
  width: '100%',
  position: 'relative',
  '&:hover': {
    backgroundColor: !disabled ? theme.palette.action.hover : undefined,
  },
  '&:focus-visible': {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: 2,
  }
}));

const SelectionList = styled(List)(({ theme }) => ({
  padding: theme.spacing(0.5),
  width: '100%',
  maxWidth: '200px',
  backgroundColor: theme.palette.background.default,
}));

const SelectionLabel = styled(Typography)(({ theme }) => ({
  fontSize: '0.75rem',
  color: theme.palette.text.secondary,
  marginBottom: theme.spacing(0.5),
}));

const getEngineIcon = (engine) => {
  switch (engine) {
    case 'guru': return guruLogo;
    case 'chatgpt': return openaiLogo;
    case 'claude': return claudeLogo;
    case 'gemini': return geminiLogo;
    case 'deepseek': return deepseekLogo;
    case 'grok': return grokLogo;
    default: return openaiLogo;
  }
};

const getServiceIcon = (service) => {
  switch (service) {
    case 'adjustText': return <AdjustIcon fontSize="small" />;
    case 'optimizePrompt': return <OptimizeIcon fontSize="small" />;
    case 'webSearch': return <SearchIcon fontSize="small" />;
    default: return <ClearIcon fontSize="small" />;
  }
};

export default function FeatureSelects({
  selectedEngine,
  onEngineChange,
  selectedService,
  setSelectedService,
  isDisabled
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { t } = useTranslation();

  // Using refs to keep track of elements for popover positioning
  const engineButtonRef = useRef(null);
  const serviceButtonRef = useRef(null);

  // Replace Menu with Popover to avoid accessibility issues
  const [enginePopoverOpen, setEnginePopoverOpen] = useState(false);
  const [servicePopoverOpen, setServicePopoverOpen] = useState(false);

  const handleEngineButtonClick = () => {
    if (!isDisabled) {
      setServicePopoverOpen(false); // Close other popover if open
      setEnginePopoverOpen(!enginePopoverOpen);
    }
  };

  const handleServiceButtonClick = () => {
    if (!isDisabled) {
      setEnginePopoverOpen(false); // Close other popover if open
      setServicePopoverOpen(!servicePopoverOpen);
    }
  };

  const handleEngineSelect = useCallback((engine) => {
    onEngineChange(engine);
    setEnginePopoverOpen(false);
  }, [onEngineChange]);

  const handleServiceSelect = useCallback((service) => {
    setSelectedService(service);
    setServicePopoverOpen(false);
  }, [setSelectedService]);

  const handlePopoverClose = (type) => {
    if (type === 'engine') {
      setEnginePopoverOpen(false);
    } else {
      setServicePopoverOpen(false);
    }
  };

  // Engine options for mobile menu
  const engineOptions = [
    { key: 'guru', text: t('engine.guru'), icon: guruLogo },
    { key: 'chatgpt', text: t('engine.chatgpt'), icon: openaiLogo },
    { key: 'claude', text: t('engine.claude'), icon: claudeLogo },
    { key: 'gemini', text: t('engine.gemini'), icon: geminiLogo },
    { key: 'deepseek', text: t('engine.deepseek'), icon: deepseekLogo },
    { key: 'grok', text: t('engine.grok'), icon: grokLogo },
  ];

  // Use standard desktop components if not on mobile
  if (!isMobile) {
    return (
      <SelectContainer>
        <EngineSelect
          sx={{ flexGrow: 1, marginBottom: 0.5, minHeight: '30px' }}
          selectedEngine={selectedEngine}
          onEngineChange={onEngineChange}
          disabled={isDisabled}
        />
        <ServiceSelect
          sx={{ flexGrow: 1, marginBottom: 0.5, minHeight: '30px' }}
          selectedService={selectedService}
          setSelectedService={setSelectedService}
          onEngineChange={onEngineChange}
          disabled={isDisabled}
        />
      </SelectContainer>
    );
  }

  // Mobile UI with compact buttons and popover selection
  return (
    <SelectContainer>
      {/* Engine Selector */}
      <Box>
        <SelectionLabel>{t('engine.selectLabel')}</SelectionLabel>
        <MobileSelectionButton
          onClick={handleEngineButtonClick}
          ref={engineButtonRef}
          aria-haspopup="listbox"
          aria-expanded={enginePopoverOpen}
          aria-label={t('engine.selectLabel')}
          disabled={isDisabled}
          selected={enginePopoverOpen}
        >
          <img
            src={getEngineIcon(selectedEngine)}
            alt=""
            style={{
              width: 24,
              height: 24,
            }}
          />
        </MobileSelectionButton>

        <Popover
          open={enginePopoverOpen}
          anchorEl={engineButtonRef.current}
          onClose={() => handlePopoverClose('engine')}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
          PaperProps={{
            sx: {
              backgroundColor: theme.palette.background.default,
              boxShadow: theme.shadows[8],
              borderRadius: theme.shape.borderRadius,
              maxHeight: 250,
              overflow: 'auto'
            }
          }}
          container={document.body} // Render in body to avoid stacking context issues
        >
          <SelectionList role="listbox" aria-labelledby="engine-selector">
            {engineOptions.map((engine) => (
              <ListItem
                key={engine.key}
                button
                selected={engine.key === selectedEngine}
                onClick={() => handleEngineSelect(engine.key)}
                dense
                role="option"
                aria-selected={engine.key === selectedEngine}
                sx={{
                  '&.Mui-selected': {
                    backgroundColor: theme.palette.action.selected,
                  },
                  '&:hover': {
                    backgroundColor: theme.palette.action.hover,
                  }
                }}
              >
                <ListItemIcon>
                  <img
                    src={engine.icon}
                    alt=""
                    style={{ width: 24, height: 24 }}
                  />
                </ListItemIcon>
                <ListItemText primary={engine.text} />
              </ListItem>
            ))}
          </SelectionList>
        </Popover>
      </Box>

      {/* Service Selector */}
      <Box>
        <SelectionLabel>{t('service.selectLabel')}</SelectionLabel>
        <MobileSelectionButton
          onClick={handleServiceButtonClick}
          ref={serviceButtonRef}
          aria-haspopup="listbox"
          aria-expanded={servicePopoverOpen}
          aria-label={t('service.selectLabel')}
          disabled={isDisabled}
          selected={servicePopoverOpen}
        >
          {getServiceIcon(selectedService)}
        </MobileSelectionButton>

        <Popover
          open={servicePopoverOpen}
          anchorEl={serviceButtonRef.current}
          onClose={() => handlePopoverClose('service')}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
          PaperProps={{
            sx: {
              backgroundColor: theme.palette.background.default,
              boxShadow: theme.shadows[8],
              borderRadius: theme.shape.borderRadius,
              maxHeight: 200,
              overflow: 'auto'
            }
          }}
          container={document.body} // Render in body to avoid stacking context issues
        >
          <SelectionList role="listbox" aria-labelledby="service-selector">
            {SERVICES.map((service) => (
              <ListItem
                key={service.key}
                button
                selected={service.key === selectedService}
                onClick={() => handleServiceSelect(service.key)}
                dense
                role="option"
                aria-selected={service.key === selectedService}
                sx={{
                  '&.Mui-selected': {
                    backgroundColor: theme.palette.action.selected,
                  },
                  '&:hover': {
                    backgroundColor: theme.palette.action.hover,
                  }
                }}
              >
                <ListItemIcon>
                  {getServiceIcon(service.key)}
                </ListItemIcon>
                <ListItemText primary={t(service.text)} />
              </ListItem>
            ))}
          </SelectionList>
        </Popover>
      </Box>
    </SelectContainer>
  );
}